import { useEffect, useMemo } from 'react';

import { getProjectByIdV2 } from '@/actions/project.action';
import type { ProjectGroupV1SchemaType } from '@/global/schemas/project.schema';
import { setProjectInfo } from '@/store/actions/project.action';
import { useAppDispatch, useAppSelector } from '@/store/utils';

import type { ProjectGroupV1Hook } from '../types/project.type';
import { useWeb3 } from '../useWeb3';

const useProjectGroupV2 = (): ProjectGroupV1Hook => {
  const { address } = useWeb3();
  const { nodeId } = useAppSelector(state => state.app);
  const { projectInfo } = useAppSelector(state => state.project);
  const dispatch = useAppDispatch();

  const formatGroupData: Array<ProjectGroupV1SchemaType> = (projectInfo?.groups ?? []).map(group => ({
    id: group.name,
    startedAt: group.openingAt,
    endedAt: group.closingAt,
    name: group.name,
    whitelist: group.whitelist,
    maximum: 0, // unused in v2
    minimum: 0,
  }));
  const groupsData = {
    data: formatGroupData,
    meta: {
      total: 10,
      size: 10,
      page: 1,
    } as any, // unused in v2
  };

  useEffect(() => {
    if (groupsData && groupsData.data.length !== 0) {
      const timeouts = groupsData.data.map(group => {
        const timeUntilEnd = new Date(group.startedAt).getTime() - new Date().getTime();

        if (timeUntilEnd > 0) {
          return setTimeout(async () => {
            const projectInfo = await getProjectByIdV2(nodeId, 'en');
            dispatch(setProjectInfo(projectInfo));
          }, timeUntilEnd);
        }

        return null;
      });

      return () => {
        timeouts.forEach((timeout: any) => clearTimeout(timeout));
      };
    }
  }, [groupsData]);

  const isStarted = useMemo(() => {
    if (groupsData && groupsData.data.length !== 0) {
      const diff = groupsData?.data[0]?.startedAt
        ? new Date(groupsData.data[0].startedAt).getTime() - new Date().getTime()
        : 0;
      return diff <= 0;
    }

    return true;
  }, [groupsData]);

  const currentGroup: ProjectGroupV1SchemaType | undefined = useMemo(() => {
    if (groupsData && groupsData.data.length !== 0) {
      const now = new Date().getTime();
      return groupsData.data.find(group => {
        return new Date(group.startedAt).getTime() <= now && new Date(group.endedAt).getTime() >= now;
      });
    }

    return undefined;
  }, [groupsData]);

  const isValidMemember = useMemo(() => {
    return isStarted && (!currentGroup || currentGroup?.whitelist?.includes(address!));
  }, [isStarted, currentGroup, address]);

  return {
    isValidMemember,
    isLoadingGroups: false,
    isFetchingGroups: false,
    groupsData,
    currentGroup,
  };
};

export { useProjectGroupV2 };
